import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate, Link } from "react-router-dom";
import PropTypes from 'prop-types';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import moment from 'moment';
import classnames from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '../../../../../app/providers/MobileContext';
import { paginationExportOptions, renderCustomHeader, tableAdjustableColExportOptions } from '../../helpers/table';
import { isInternalUser } from '../../helpers/constants';
import { eventStatusFormatter, eventTypeDetection } from '../../helpers/helperEvents';
import { hasChannelsRestriction } from '../../helpers/helperUsers';
import { formatTimestamp } from '../../helpers/tablesFuncHelpers';
import momenttz from '../../helpers/momenttz';
import { formatWithZoneAbbr, formatWithZoneAbbrNode, isTimezoneSyncEnabled } from '../../helpers/timeDisplaySettings';
import { eventIcons, generalIcons } from '../../helpers/iconHelpers';
import IconTooltip from '../../dump/menuComponents/IconTooltip';
import CustomButton from '../../CustomButton';
import MobileEventCard from './MobileEventCard';
import MobileEventPagination from './MobileEventPagination';
import '../../common/MobileGridCards.sass';

const EventClipsTable = forwardRef((props, ref) => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'eventsPage.eventClipsContainer.eventClipsTable' });
	const isMobile = useIsMobile(991);
	const tzSync = isTimezoneSyncEnabled();
	const dateTimeLabel = t(tzSync ? 'labelDateTimeLocal' : 'labelDate/Time');
	const dateTimeColumnTooltip = tzSync && <IconTooltip description={t('descriptionDateTimeColumn')} id="date-time-column-tooltip" />;

	const COLUMNS_WIDTH = {
		0: {
			// 'Date / Time': '12%', Type: '12%', Status: '12%', Vehicle: '15%', Groups: '14%', Note: '7%', Reviewed: '10%', ID: '8%', Duration: '8%', RequestedAt: '12%', LastReviewed: '12%', LastReviewedBy: '14%', LastViewed: '12%', LastViewedBy: '12%'
			'Date / Time': '7%', Type: '7%', Status: '5%', Vehicle: '6%', Groups: '6%', Feedback: '5%', Note: '4%', ID: '4%', Duration: '4%', Driver: '5%', RequestedAt: '6%', RequestedBy: '6%', LastReviewed: '6%', LastReviewedBy: '6%', LastViewed: '6%', LastViewedBy: '6%'
		},
		1: {
			// 'Date / Time': '12%', Type: '12%', Status: '12%', Vehicle: '12%', Groups: '10%', Note: '6%', Reviewed: '12%', ID: '7%', Duration: '7%', RequestedAt: '12%', Driver: '8%', LastReviewed: '12%', LastReviewedBy: '14%', LastViewed: '12%', LastViewedBy: '12%'
			'Date / Time': '7%', Type: '7%', Status: '5%', Vehicle: '6%', Groups: '6%', Feedback: '5%', Note: '4%', ID: '4%', Duration: '4%', Driver: '5%', RequestedAt: '6%', RequestedBy: '6%', LastReviewed: '6%', LastReviewedBy: '6%', LastViewed: '6%', LastViewedBy: '6%'
		},
	};

	const CSV_HEADERS = [
		{ label: t('labelTime'), key: "timestamp" },
		{ label: t('labelEventType'), key: "type" },
		{ label: t('labelStatus'), key: "uploading_status" },
		{ label: t('labelLabel'), key: "event_label" },
		{ label: t('labelVehicleName'), key: "vehicle_name" },
		{ label: t('labelGroups'), key: "groups" },
		{ label: t('labelFeedback'), key: "feedback" },
		{ label: t('labelReviewedBy'), key: "last_viewed" },
		{ label: t('labelID'), key: "id" },
		{ label: t('labelDuration'), key: "duration" },
		{ label: t('labelRequestedAt'), key: "create_time" },
		{ label: t('labelRequestedBy'), key: "creator_name" },
		{ label: t('labelDriverName'), key: "driver_name" },
		{ label: t('labelNote'), key: "event_notes" },
		{ label: t('lastReviewed'), key: "last_reviewed" },
		{ label: t('lastReviewedBy'), key: "last_reviewed_name" },
		{ label: t('lastViewedDate'), key: "last_viewed_date" },
		{ label: t('lastViewedBy'), key: "last_viewed_by" },
	];

	const { user, columns, company, events, isWSCCompany, selectedRow, totalSize, eventDate, setDashboardRedirect, openEventNote, onFlagEvent, openEventVideoAll, downloadNVR, openShareEvent, openEventSnapshot, openModalDelete, downloadFlood, handlePageChange, handleSizePerPageChange, handleSortChange, page, sizePerPage, sortName, sortOrder, editVehicle, restoreWCSEvent, webfleetDeviceId, getAllFleetEvents, setEventArchiveRestoring, getFleetEvents } = props
	const userRole = user.roles[0]
	const userPermissions = user.permissions
	const canDownloadNVR = !hasChannelsRestriction(user)
	const options = {
		...paginationExportOptions,
		onPageChange: handlePageChange,
		onSizePerPageList: handleSizePerPageChange,
		page,
		sizePerPage,
		sortName,
		sortOrder,
		onSortChange: handleSortChange,
	}
	const driverFeatureAccess = (company.driver_recognition !== 'manual' || ['system_admin'].indexOf(userRole) > -1)
	const reviewedByFeatureAccess = (['system_admin', 'customer_service', 'partner', 'fleet_manager', 'fleet_maintainer', 'partner_view', 'group_manager'].indexOf(userRole) > -1)
	const tableColumnsWidth = COLUMNS_WIDTH[driverFeatureAccess * 1]

	// Export button
	const refExportBtn = useRef()
	const [csvData, setCsvData] = useState([]);

	useEffect(() => {
		if (csvData.length > 0) {
			refExportBtn.current.link.click()
		}
	}, [csvData])

	const timestampFormatter = (cell, row) => {
		if (cell) {
			return formatWithZoneAbbrNode(momenttz(`${cell}+00`, row?.device_tz), 'M/DD/YYYY hh:mm:ss A');
		}
		return t('textNotReviewed');
	}

	const csvTimestampFormatter = (cell, row) => {
		if (cell) {
			return formatWithZoneAbbr(momenttz(`${cell}+00`, row?.device_tz), 'M/DD/YYYY hh:mm:ss A')
		}
		return t('textNotReviewed');
	}

	const durationFormatter = (cell, row) => {
		// FWSD-4819
		if (eventStatusFormatter(row.uploading_status) !== 'Uploaded' && row.uploading_status !== 'PARTIAL_VIDEO') {
			return '0'
		}

		let clipDuration = row.duration
		// FWSD-4558
		if (row.accurate_duration) {
			clipDuration = row.accurate_duration
		}

		return moment.duration(clipDuration, 'seconds').format('h [hrs] m [min] s [sec]', { trim: 'both' })
	}

	const driverFormatter = (cell, row) => row.driver_name || row.geotab_driver

	const vehicleFormatter = (cell, row) => {
		return isInternalUser(userRole)
			? <CustomButton variant="link" onClick={() => editVehicle(row.device_id)}>{cell || row.device_id}</CustomButton>
			: (cell || row.device_id);
	}

	const vehicleCSVFormatter = (cell, row) => row.vehicle_name || row.device_id

	const videostatusFormatter = (cell, row) => {
		if (row.size) {
			return `${(row.size / 1024 / 1024).toFixed(2)} MB `
		}
		if (row.uploading_status && row.uploading_status.toLowerCase() === 'event_reported') {
			return t('textPending');
		}
		if (!row.uploading_status || row.uploading_status.toLowerCase() === 'video_non_exist') {
			return t('textUnavailable');
		}

		const re = /_/gi;
		return row.uploading_status.replace(re, ' ')
	}

	const typeFormatter = (cell, row) => {
		return i18n.t(`constants.eventNames.${eventTypeDetection(row).replace(/\s/g, "_")}`, eventTypeDetection(row));
	}

	const reviewedByFormatter = (cell, row) => {
		const res = []
		if (row.last_viewed.viewer.first_name) {
			res.push(row.last_viewed.viewer.first_name)
		}
		if (row.last_viewed.viewer.last_name) {
			res.push(row.last_viewed.viewer.last_name)
		}
		if (res.length > 0 && row.last_viewed.viewed_ts) {
			res.push('on')
		}
		if (row.last_viewed.viewed_ts) {
			res.push(formatWithZoneAbbr(momenttz(`${row.last_viewed.viewed_ts}+00`, row.device_tz), 'M-D-YYYY'))
		}

		if (!res.length) {
			return t('textNotReviewed');
		}

		return res.join(' ')
	}

	const feedbackFormatter = (cell, row) => {
		if (cell) {
			return <label style={{ width: "1.5rem", height: "1.5rem" }}>{eventIcons.eventFeedbackIcon}</label>
		} else {
			return
		}
	}

	const lastViewedDateFormatter = (cell, row) => {
		const ts = cell?.viewed_ts;
		return ts ? formatWithZoneAbbrNode(momenttz(`${ts}+00`, row?.device_tz), 'M/DD/YYYY hh:mm:ss A') : t('textNotReviewed');
	}

	const lastViewedByFormatter = (cell) => {
		const viewer = cell?.viewer;
		const viewerName = `${viewer?.first_name || ''} ${viewer?.last_name || ''}`;
		return viewerName === 'Fake Email' ? t('externalUser') : viewerName;
	}

	const lastReviewedByFormatter = (cell) => {
		return cell === 'Fake Email' ? t('externalUser') : cell;
	}

	const driverNameSort = name => (a, b, order) => {
		const first = a.driver_name || a.geotab_driver || '';
		const second = b.driver_name || b.geotab_driver || '';

		return order === 'asc' ? second.localeCompare(first) : first.localeCompare(second)
	}

	const reviewedBySort = name => (a, b, order) => {
		const first = a.last_viewed.viewer.first_name || a.last_viewed.viewer.last_name || '';
		const second = b.last_viewed.viewer.first_name || b.last_viewed.viewer.last_name || '';

		return order === 'asc' ? second.localeCompare(first) : first.localeCompare(second)
	}

	const redirectToTripReplay = (eventData) => {
		setDashboardRedirect({
			type: 'redirectFromDailyEvents',
			device_id: eventData.device_id,
			timestamp: moment(eventData.timestamp).utc().format('YYYY-MM-DD HH:mm:ss'),
		})
		navigate('/');
	}

	const tripFormatter = (cell, row) => {
		return <Link to="/reports" onClick={(e) => { e.preventDefault(); redirectToTripReplay(row); }}>{t('labelViewTrip')}</Link>
	}

	const actionsFormatterWCS = (cell, row) => {
		let buttons, playButton, downloadButton, deleteButton, shareButton;

		let noteButton = (
			<CustomButton size="icon" variant="tertiary" prefix={classnames("note-button", "btn", "btn-default", { 'with-note': row.event_notes })} active={row.event_notes} onClick={() => openEventNote(row)}>
				{t('btnNote')}
			</CustomButton>
		)

		if (row.type.toLowerCase() === 'custom clip' && row.uploading_status === 'EVENT_REPORTED') {
			buttons = (
				<div>
					<CustomButton id="deleteButton" prefix="imageButton deleteButton" title={t('btnDeleteCustomRequest')} onClick={() => openModalDelete(row.id)}>
						{generalIcons.crossDelete}
					</CustomButton>
				</div>
			);

			if (['custom_user', 'custom_partner'].includes(userRole) && !_.find(userPermissions, { perm_category_id: 2, a_delete: true })) {
				buttons = null
			}
		}
		else {
			switch (row.status) {
				case 'archived':
					buttons = (
						<CustomButton prefix="imageButton requestButton" title={t('btnRequest')} onClick={() => restoreWCSEvent(row)} />
					);
					break;
				case 'restoring':
					buttons = (
						<div>
							<p>{t('textRestoringProgress')}</p>
						</div>
					);
					break;
				default:
					if (row.uploading_status === 'VIDEO_UPLOADED' || row.uploading_status === 'PARTIAL_VIDEO') {
						playButton = <CustomButton prefix="imageButton playButton" title={t('btnPlayVideo')} onClick={() => openEventVideoAll(row)} />
						downloadButton = canDownloadNVR && <CustomButton prefix="imageButton downloadButton" title={t('btnDownloadVideo')} onClick={() => downloadNVR(row.id)} />
						shareButton = <CustomButton prefix="imageButton shareButton" title={t('btnShareEvent')} onClick={() => openShareEvent(row)} />
					}
					else {
						playButton = <CustomButton prefix="imageButton playButton" title={t('btnPlayVideo')} disabled="disabled" />
						downloadButton = canDownloadNVR && <CustomButton prefix="imageButton downloadButton" title={t('btnDownloadVideo')} disabled="disabled" />
					}

					if (['system_admin', 'customer_service', 'partner', 'fleet_manager'].indexOf(userRole) > -1) {
						deleteButton = <CustomButton id="deleteButton" prefix="imageButton deleteButton" title={t('btnDeleteCustomRequest')} onClick={() => openModalDelete(row.id)}>
							{generalIcons.crossDelete}
						</CustomButton>
					}
					else if ((row.uploading_status === 'VIDEO_UPLOADED' || row.uploading_status === 'PARTIAL_VIDEO') && row.type.toLowerCase() === 'flood') {
						downloadButton = <CustomButton prefix="imageButton downloadButton" title={t('btnDownloadVideo')} onClick={() => downloadFlood(row)} />
					}

					if (['custom_user', 'custom_partner'].includes(userRole)) {
						if (!_.find(userPermissions, { perm_category_id: 9, a_view: true })) {
							downloadButton = null
						}
					}

					buttons = (
						<div className="event-actions">
							{playButton}
							{downloadButton}
							{shareButton}
							{noteButton}
							{deleteButton}
						</div>
					);
					break;
			}
		}

		return buttons;
	}

	const actionsFormatter = (cell, row, isMobile) => {
		const isStatusArchived = row.archive === 'archived';
		const isStatusRestoring = row.archive === 'restoring';
		const isEventArchived = isStatusArchived || isStatusRestoring;
		const isEventFlagged = row.flagged;
		const actionItems = {
			archivedButton: null,
			playButton: null,
			snapshotButton: null,
			shareButton: null,
			downloadButton: null,
			flaggedButton: null,
			noteButton: null,
			deleteButton: null,
		};

		if (!isEventArchived) {
			if (row.uploading_status === 'VIDEO_UPLOADED' || row.uploading_status === 'PARTIAL_VIDEO') {
				actionItems.playButton = {
					label: t('viewBtn'),
					onClick: () => openEventVideoAll(row, 'Table')
				}
				if (canDownloadNVR) {
					actionItems.downloadButton = {
						label: t('btnDownloadVideo'),
						onClick: () => downloadNVR(row.id)
					}
				}
				if (
					!(['system_admin', 'customer_service', 'partner', 'fleet_manager',].includes(userRole) ||
						(
							['custom_user', 'custom_partner'].includes(userRole) &&
							_.find(userPermissions, { 'perm_category_id': 2, 'a_delete': true }))
					)
					&& row.type.toLowerCase() === 'flood'
				) {
					actionItems.downloadButton = {
						label: t('btnDownloadVideo'),
						onClick: () => downloadFlood(row)
					}
				}
			}
			if (row.snapshot_status === 'SNAPSHOT_UPLOADED') {
				actionItems.snapshotButton = {
					label: t('btnViewSnapshot'),
					onClick: () => openEventSnapshot(row)
				}
			}
			if (['custom_user', 'custom_partner'].includes(userRole)) {
				if (!_.find(userPermissions, { perm_category_id: 9, a_view: true })) {
					actionItems.downloadButton = null
				}
			}
		} else {
			if (isStatusArchived) {
				actionItems.archivedButton = {
					label: t('btnRestoreEvent'),
					onClick: () => setEventArchiveRestoring({ showModal: true, message_type: 'event_restore', event_id: row.id, callback: getFleetEvents })
				}
			} else if (isStatusRestoring) {
				actionItems.archivedButton = {
					className: 'rosco-tertiary active',
					label: t('btnEventRestoring'),
					onClick: () => setEventArchiveRestoring({ showModal: true, message_type: 'event_restoring', event_id: row.id, request_ts: row.request_ts })
				}
			}
		}
		if (!(['custom_user', 'custom_partner'].includes(userRole) && _.find(userPermissions, { 'perm_category_id': 2, 'a_edit': false }))) {
			actionItems.flaggedButton = {
				className: isEventFlagged ? 'on' : '',
				label: `${isEventFlagged ? t('titleUnflag') : t('titleFlag')} ${t('titleEvent')}`,
				onClick: () => onFlagEvent(row, 'Grid')
			}
			actionItems.noteButton = {
				className: row.event_notes ? 'with-note' : '',
				children: t('btnNote'),
				onClick: () => openEventNote(row)
			}
		}

		if (row.uploading_status === 'VIDEO_UPLOADED' || row.uploading_status === 'PARTIAL_VIDEO' || row.snapshot_status === 'SNAPSHOT_UPLOADED') {
			actionItems.shareButton = {
				label: t('btnShareEvent'),
				onClick: () => openShareEvent(row)
			}
		}

		if (['system_admin', 'customer_service', 'partner', 'fleet_manager',].indexOf(userRole) > -1 || (['custom_user', 'custom_partner'].includes(userRole) && _.find(userPermissions, { 'perm_category_id': 2, 'a_delete': true }))) {
			actionItems.deleteButton = {
				label: t('btnDeleteEvent'),
				children: generalIcons.crossDelete,
				onClick: () => openModalDelete(row.id)
			}
		}

		if (isMobile) {
			return actionItems;
		}

		return (
			<div className="event-actions event-actions-table">
				{
					Object.entries(actionItems)
						.filter(([, item]) => item !== null)
						.map(([key, item]) => (
							<CustomButton
								{...(key === 'noteButton' && {
									variant: "tertiary",
									size: "icon",
									active: row.event_notes
								})}
								key={key}
								id={key}
								prefix={`${key !== 'noteButton' ? 'imageButton' : 'note-button'} ${key} ${item.className || ''}`}
								title={item.label || ''}
								onClick={() => item.onClick()}
							>
								{item.children || ''}
							</CustomButton>
						))
				}
			</div>
		)
	}

	const onExportClick = async () => {
		const events = await getAllFleetEvents()
		const newCsvData = []
		events.forEach((item) => {
			const obj = {
				id: item.id,
				device_id: item.device_id,
				vehicle_name: item.vehicle_name || item.device_id,
				timestamp: csvTimestampFormatter(item.timestamp, item),
				type: i18n.t(`constants.eventNames.${eventTypeDetection(item).replace(/\s/g, "_")}`, eventTypeDetection(item)),
				uploading_status: eventStatusFormatter(item.uploading_status),
				groups: item.groups,
				last_viewed: reviewedByFormatter(item.last_viewed, item),
				duration: durationFormatter(item.duration, item),
				create_time: csvTimestampFormatter(item.create_time, item),
				creator_name: item.creator_name || '',
				driver_name: driverFormatter(item.driver_name, item),
				event_notes: item.event_notes || '',
			}

			newCsvData.push(obj)
		})

		setCsvData(newCsvData)
	}

	useImperativeHandle(ref, () => ({
		triggerExport: onExportClick
	}))

	const { label, status, vehicle, groups, feedback, id, duration, requested_at, requested_by, driver, trip, last_reviewed, last_reviewed_name, last_viewed_date, last_viewed_by } = columns;

	const csvLinkExport = (
		<CSVLink
			ref={refExportBtn}
			filename={`${t('textDailyEvents') + moment(eventDate).format('M-D-YY')}.csv`}
			headers={CSV_HEADERS}
			data={csvData}
		/>
	);

	return (
		<div className="event-page-table">
			{isMobile ? (
				<>
					{csvLinkExport}
					<div className="mobile-grid-cards-container">
						{events.map((event, i) => (
							<MobileEventCard
								key={`event_mobile_card_${event.id || i}`}
								event={event}
								typeFormatter={typeFormatter}
								timestampFormatter={timestampFormatter}
								durationFormatter={durationFormatter}
								actionsFormatter={actionsFormatter}
								tripFormatter={tripFormatter}
							/>
						))}
					</div>
					<MobileEventPagination page={page} sizePerPage={sizePerPage} totalSize={totalSize} onPageChange={handlePageChange} />
				</>
			) : (!isWSCCompany
				? (
					<div className="blockWithDropdown">
						{csvLinkExport}

						<BootstrapTable
							data={events}
							{...tableAdjustableColExportOptions}
							remote
							fetchInfo={{ dataTotalSize: totalSize }}
							selectRow={selectedRow}
							// options={{ ...options, onExportToCSV: () => allEvents }}
							// options={{ ...options, onExportToCSV:  async () => await handleExportToCSV().then(res => res.data.response.events) }}
							options={{ ...options }}
							// csvFileName={`daily_events_${moment(eventDate).format('M-D-YY')}.csv`}
							exportCSV={false}
						>
							<TableHeaderColumn
								width={tableColumnsWidth['Date / Time']}
								dataField="timestamp"
								dataSort
								dataFormat={timestampFormatter}
								csvFormat={csvTimestampFormatter}
								csvHeader={moment(eventDate).format('MM/DD/YYYY')}
								tdStyle={{ whiteSpace: 'normal' }}
							>
								{renderCustomHeader(0, dateTimeLabel, dateTimeColumnTooltip)}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.Type}
								dataField="type"
								dataSort
								dataFormat={typeFormatter}
								csvFormat={typeFormatter}
								csvHeader={t('labelEventType')}
								tdStyle={{ whiteSpace: 'normal' }}
							>
								{renderCustomHeader(1, t('labelType'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								hidden={isInternalUser(userRole) ? !status : true}
								width={tableColumnsWidth.Status}
								dataField="uploading_status"
								dataSort
								dataFormat={(e) => e ? i18n.t(`homePage.vehicleSidebar.vehicleSidebarFilter.${eventStatusFormatter(e).replace(/\s/g, "_")}`) : ''}
								csvFormat={(e) => e ? i18n.t(`homePage.vehicleSidebar.vehicleSidebarFilter.${eventStatusFormatter(e).replace(/\s/g, "_")}`) : ''}
								csvHeader={t('labelStatus')}
								tdStyle={{ whiteSpace: 'normal' }}
							>
								{renderCustomHeader(2, t('labelStatus'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width="5%"
								dataField="event_label"
								dataSort
								csvHeader={t('labelLabel')}
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!label}
							>
								{renderCustomHeader(3, t('labelLabel'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.Vehicle}
								dataField="vehicle_name"
								dataSort
								dataFormat={vehicleFormatter}
								csvFormat={vehicleCSVFormatter}
								csvHeader={t('labelVehicleName')}
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!vehicle}
							>
								{renderCustomHeader(2, t('labelVehicle'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.Groups}
								dataField="groups"
								csvHeader={t('labelGroups')}
								dataSort
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!groups}
							>
								{renderCustomHeader(3, t('labelGroups'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.Feedback}
								dataField="has_feedback"
								csvHeader={t('labelFeedback')}
								dataFormat={feedbackFormatter}
								tdStyle={{ textAlign: "center" }}
								hidden={!feedback}
							>
								{renderCustomHeader(4, t('labelFeedback'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.ID}
								dataField="id"
								csvHeader={t('labelID')}
								dataSort
								isKey
								hidden={!id}
							>
								{renderCustomHeader(4, t('labelID'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.Duration}
								dataField="duration"
								dataSort
								dataFormat={durationFormatter}
								csvFormat={durationFormatter}
								csvHeader={t('labelDuration')}
								hidden={!duration}
							>
								{renderCustomHeader(6, t('labelDuration'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.RequestedAt}
								dataField="create_time"
								dataSort
								dataFormat={timestampFormatter}
								csvFormat={csvTimestampFormatter}
								csvHeader={t('labelRequestedAt')}
								hidden={!requested_at}
							>
								{renderCustomHeader(7, t('labelRequestedAt'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.RequestedBy}
								dataField="creator_name"
								dataFormat={(cell) => cell || ''}
								csvFormat={(cell) => cell || ''}
								csvHeader={t('labelRequestedBy')}
								hidden={!requested_by}
							>
								{renderCustomHeader(11, t('labelRequestedBy'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								hidden={driverFeatureAccess ? !driver : true}
								width={tableColumnsWidth.Driver}
								dataField="driver_name"
								dataSort
								dataFormat={driverFormatter}
								csvHeader={t('labelDriverName')}
								tdStyle={{ whiteSpace: 'normal' }}
								sortFunc={driverNameSort()}
							>
								{renderCustomHeader(8, t('labelDriver'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								hidden={!(webfleetDeviceId.length > 0 || (['custom_user', 'custom_partner'].includes(userRole) && _.find(userPermissions, { 'perm_category_id': 1, 'a_view': false }))) ? !trip : true}
								width="5%"
								dataFormat={tripFormatter}
								tdStyle={{ whiteSpace: 'normal' }}
								export={false}
								className="hidden"
							>
								{renderCustomHeader(9, t('labelTrip'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.LastViewed}
								dataField="last_viewed"
								dataFormat={lastViewedDateFormatter}
								csvFormat={csvTimestampFormatter}
								csvHeader={t('lastViewedDate')}
								dataSort
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!last_viewed_date}
							>
								{renderCustomHeader(10, t('lastViewedDate'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.LastViewedBy}
								dataField="last_viewed"
								dataFormat={lastViewedByFormatter}
								csvHeader={t('lastViewedBy')}
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!last_viewed_by}
							>
								{renderCustomHeader(10, t('lastViewedBy'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.LastReviewed}
								dataField="last_reviewed"
								dataFormat={timestampFormatter}
								csvFormat={csvTimestampFormatter}
								csvHeader={t('lastReviewed')}
								dataSort
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!last_reviewed}
							>
								{renderCustomHeader(10, t('lastReviewed'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width={tableColumnsWidth.LastReviewedBy}
								dataField="last_reviewed_name"
								dataFormat={lastReviewedByFormatter}
								csvHeader={t('lastReviewedBy')}
								tdStyle={{ whiteSpace: 'normal' }}
								hidden={!last_reviewed_name}
							>
								{renderCustomHeader(10, t('lastReviewedBy'))}
							</TableHeaderColumn>
							<TableHeaderColumn
								width="7%"
								dataFormat={actionsFormatter}
								dataField="event_notes"
								csvFormat={cell => cell || ''}
								csvHeader={t('labelNote')}
								tdStyle={{ whiteSpace: 'normal' }}
							>
								<div className="custom-table-header-row">
									<div className="custom-table-header-title">{t('titleActions')}</div>
								</div>
							</TableHeaderColumn>
						</BootstrapTable>
					</div>
				) : (
					<BootstrapTable
						data={events}
						{...tableAdjustableColExportOptions}
						remote
						fetchInfo={{ dataTotalSize: totalSize }}
						selectRow={selectedRow}
						options={options}
						csvFileName={`${t('textDailyEvents') + moment(eventDate).format('M-D-YY')}.csv`}
					>
						<TableHeaderColumn
							width="11%"
							dataField="timestamp"
							dataSort
							dataFormat={timestampFormatter}
							csvFormat={csvTimestampFormatter}
							csvHeader={moment(eventDate).format('MM/DD/YYYY')}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							{renderCustomHeader(0, dateTimeLabel, dateTimeColumnTooltip)}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="10%"
							dataField="type"
							dataSort
							tdStyle={{ whiteSpace: 'normal' }}
							csvHeader={t('labelEventType')}
							csvFormat={typeFormatter}
							dataFormat={typeFormatter}
						>
							{renderCustomHeader(1, t('labelType'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="9%"
							dataField="vehicle_name"
							dataSort
							dataFormat={vehicleFormatter}
							csvFormat={vehicleCSVFormatter}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							{renderCustomHeader(2, t('labelVehicle'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="6%"
							dataField="groups"
							dataSort
							csvHeader={t('labelGroup')}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							{renderCustomHeader(3, t('labelGroup'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="5%"
							dataField="has_feedback"
							csvHeader={t('labelFeedback')}
							dataFormat={feedbackFormatter}
							tdStyle={{ textAlign: "center" }}
							hidden={!feedback}
						>
							{renderCustomHeader(4, t('labelFeedback'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="8%"
							dataField="size"
							dataSort
							dataFormat={videostatusFormatter}
							csvHeader={t('labelFileSize')}
							csvFormat={videostatusFormatter}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							{renderCustomHeader(4, t('labelFileSize'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="5%"
							dataField="id"
							dataSort
							csvHeader={t('labelID')}
							isKey
						>
							{renderCustomHeader(7, t('labelID'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="7%"
							dataField="start_ts"
							dataSort
							dataFormat={formatTimestamp}
							csvHeader={t('labelStart')}
							csvFormat={formatTimestamp}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							{renderCustomHeader(8, t('labelStart'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="7%"
							dataField="end_ts"
							dataSort
							dataFormat={formatTimestamp}
							csvHeader={t('labelEnd')}
							csvFormat={formatTimestamp}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							{renderCustomHeader(9, t('labelEnd'))}
						</TableHeaderColumn>
						<TableHeaderColumn
							width="11%"
							dataField="event_notes"
							dataFormat={actionsFormatterWCS}
							tdStyle={{ whiteSpace: 'normal' }}
						>
							<div className="custom-table-header-row">
								<div className="custom-table-header-title">{t('titleActions')}</div>
							</div>
						</TableHeaderColumn>
					</BootstrapTable>
				))}
		</div>
	)
})

EventClipsTable.propTypes = {
	user: PropTypes.objectOf(PropTypes.any).isRequired,
	company: PropTypes.objectOf(PropTypes.any).isRequired,
	events: PropTypes.arrayOf(PropTypes.any).isRequired,
	isWSCCompany: PropTypes.bool.isRequired,
	selectedRow: PropTypes.objectOf(PropTypes.any).isRequired,
	totalSize: PropTypes.number.isRequired,
	eventDate: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.instanceOf(moment),
	]),
	page: PropTypes.number.isRequired,
	sizePerPage: PropTypes.number.isRequired,
	sortName: PropTypes.string.isRequired,
	sortOrder: PropTypes.string.isRequired,
	setDashboardRedirect: PropTypes.func.isRequired,
	openEventNote: PropTypes.func.isRequired,
	onFlagEvent: PropTypes.func.isRequired,
	openEventVideoAll: PropTypes.func.isRequired,
	downloadNVR: PropTypes.func.isRequired,
	openShareEvent: PropTypes.func.isRequired,
	openEventSnapshot: PropTypes.func.isRequired,
	openModalDelete: PropTypes.func.isRequired,
	downloadFlood: PropTypes.func.isRequired,
	handlePageChange: PropTypes.func.isRequired,
	handleSizePerPageChange: PropTypes.func.isRequired,
	handleSortChange: PropTypes.func.isRequired,
	editVehicle: PropTypes.func.isRequired,
	restoreWCSEvent: PropTypes.func.isRequired,
	webfleetDeviceId: PropTypes.string,
}

EventClipsTable.defaultProps = {
	webfleetDeviceId: '',
}

const mapStateToProps = ({ user, company, webfleetData }) => ({
	user: user.user,
	company: company.company,
	webfleetDeviceId: webfleetData.deviceId,
});

const mapDispatchToProps = dispatch => ({
	setDashboardRedirect: (state) => {
		dispatch({ type: 'SET_DASHBOARD_REDIRECT', payload: state })
	},
	setEventArchiveRestoring: (state) => {
		dispatch({ type: 'SET_EVENT_ARCHIVE_RESTORING', payload: state });
	},
})

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(EventClipsTable);
